import React, { useState, useRef, useCallback, useEffect } from 'react';
import classnames from 'classnames';

import Analytics from '@hh.ru/analytics-js';
import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { usePush } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonKind } from 'bloko/blocks/button';
import { FormError } from 'bloko/blocks/form';
import VSpacing from 'bloko/blocks/vSpacing';
import urlParser from 'bloko/common/urlParser';

import { useChatikLink } from 'src/hooks/useChatikLink';
import UserTypes from 'src/types/userTypes';
import PostMessage, { DataResize, DataCallTime } from 'src/utils/PostMessage';
import fetcher from 'src/utils/fetcher';

import styles from './index.less';

const URL_CREATE = '/applicant-services/complete_resume/create/' as const;
const URL_BACKOFFICE = '/admin/complete_resume/order/' as const;
const MAX_FRAME_HEIGHT = 2000; // максимальная высота фрейма (чтобы точно поместилось содержимое), в событии planerka:widget:resize придет реальная
const IFRAME_WAITING_INTERVAL = 5000; // время ожидания прогрузки фрейма планерки

declare global {
    interface FetcherPostApi {
        [k: `${typeof URL_CREATE}${string}`]: {
            body: void;
            queryParams: void;
            response: void;
        };
    }
}

const TrlKeys = {
    backError: 'applicantServices.createRequest.backError',
    waitingError: 'applicantServices.createRequest.waitingError',
    reloadPage: 'applicantServices.createRequest.reloadPage',
};

let waitingTimeout: ReturnType<typeof setTimeout>;

const RequestPlanerkaFrame: TranslatedComponent<{ src: string }> = ({ src, trls }) => {
    const userType = useSelectorNonNullable((state) => state.userType);
    const pathname = useSelectorNonNullable((state) => state.router.location.pathname);
    const order = useSelectorNonNullable(({ careerServiceOrder }) => careerServiceOrder);
    const planerkaFrameFail = useSelectorNonNullable(({ planerkaFrameFail }) => planerkaFrameFail);
    const isOpenChat = useSelectorNonNullable(({ isOpenChat }) => isOpenChat);

    const [showPlanerka, setShowPlanerka] = useState(true);
    const planerkaRef = useRef<HTMLDivElement>(null);
    const refIframe = useRef<HTMLIFrameElement>(null);
    const [widthIframe, setWidthIframe] = useState('');
    const [heightIframe, setHeightIframe] = useState('');
    const [hasError, setHasError] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [waiting, setWaiting] = useState(true);
    const push = usePush();
    const getChatikLink = useChatikLink();

    const triggerWidgetResize = useCallback(() => {
        // отправляем планерке размеры, в которые должен поместиться айфрейм
        if (!planerkaRef.current || !refIframe.current || !refIframe.current.contentWindow) {
            return;
        }
        const wrapperWidth = planerkaRef.current.offsetWidth;
        PostMessage.trigger({
            target: refIframe.current.contentWindow,
            message: { event: 'planerka:parent:resize', width: wrapperWidth, height: MAX_FRAME_HEIGHT },
        });
    }, []);

    useEffect(() => {
        window.addEventListener('resize', triggerWidgetResize);
        return () => {
            window.removeEventListener('resize', triggerWidgetResize);
        };
    }, [triggerWidgetResize]);

    useEffect(() => {
        waitingTimeout = setTimeout(() => {
            setWaiting(false);
            Analytics.sendEvent('planerka', 'frame_fail_timeout');
            if (planerkaFrameFail) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                test.error.function();
            }
        }, IFRAME_WAITING_INTERVAL);
    }, [fetching, planerkaFrameFail]);

    useEffect(() => {
        PostMessage.on({
            origin: urlParser(src).host,
            handlers: {
                'planerka:widget:sizes': () => {
                    // запрос размеров родительского окна
                    triggerWidgetResize();
                    setFetching(true);
                    clearTimeout(waitingTimeout);
                },
                'planerka:booking:create': (data: DataCallTime) => {
                    // успешное создание встречи, добавляем к отправке на бэк время встречи, чтобы не ждать хук от планерки
                    if (isOpenChat && order.chatId !== null) {
                        setShowPlanerka(false);
                    }
                    window.scrollTo(0, 0);
                    const formData = new FormData();
                    formData.append('callTimeBegin', data.start);
                    fetcher
                        .postFormData(`${URL_CREATE}${order.orderCode}`, formData)
                        .then(() => {
                            if (isOpenChat && order.chatId !== null) {
                                setShowPlanerka(false);
                                push(getChatikLink(order.chatId));
                            }
                        })
                        .catch(() => {
                            setHasError(true);
                        });
                },
                'planerka:widget:resize': (data: DataResize) => {
                    // ресайз окна виджета, подставляем айфрейму реальные размеры
                    setWidthIframe(`${data.width}px`);
                    setHeightIframe(`${data.height}px`);
                    setFetching(true);
                    clearTimeout(waitingTimeout);
                },
                'planerka:booking:ended': () => {
                    // клик по кнопке “Завершить” на странице после успешной записи, закрываем виджет
                    setShowPlanerka(false);
                    if (userType === UserTypes.Backoffice) {
                        push(`${URL_BACKOFFICE}${order.orderCode}?applicant_id=${order.applicant.id}`);
                    } else {
                        push(pathname.replace('/edit', ''));
                    }
                },
            },
        });
    }, [src, order, pathname, userType, triggerWidgetResize, fetching, isOpenChat, getChatikLink, push]);

    if (!showPlanerka) {
        return null;
    }

    return (
        <div ref={planerkaRef}>
            {!fetching && waiting && (
                <div className={styles.loading}>
                    <Loader size={24} />
                </div>
            )}
            <iframe
                src={src}
                width={widthIframe}
                height={heightIframe}
                ref={refIframe}
                className={classnames({
                    [styles.loadingFrame]: !fetching || !waiting,
                })}
            />
            {!waiting && (
                <>
                    <FormError show={!waiting}>{trls[TrlKeys.waitingError]}</FormError>
                    <VSpacing base={6} />
                    <Button
                        kind={ButtonKind.Primary}
                        onClick={() => {
                            push(window.location.href);
                        }}
                    >
                        {trls[TrlKeys.reloadPage]}
                    </Button>
                </>
            )}
            <FormError show={hasError}>
                <VSpacing base={2} />
                {trls[TrlKeys.backError]}
            </FormError>
        </div>
    );
};

export default translation(RequestPlanerkaFrame);
